import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { fetchReady } from '../api/rmf-client';
import { BackendStatusContext } from './BackendStatusContext';

export type BackendReadiness = 'unknown' | 'healthy' | 'degraded' | 'unreachable';
export type WebSocketState = 'idle' | 'connecting' | 'open' | 'closed' | 'stale';

export interface BackendStatus {
  readiness: BackendReadiness;
  httpReachable: boolean;
  readinessDetail: string | null;
  ws: WebSocketState;
  wsLastEventAt: number | null;
  staleSince: number | null;
}

const DEFAULT_STATUS: BackendStatus = {
  readiness: 'unknown',
  httpReachable: false,
  readinessDetail: null,
  ws: 'idle',
  wsLastEventAt: null,
  staleSince: null,
};

export function BackendStatusProvider({ children }: { children: ReactNode }) {
  const [status, setStatusState] = useState<BackendStatus>(DEFAULT_STATUS);

  const setStatus = useMemo(
    () =>
      (patch: Partial<BackendStatus>) =>
        setStatusState((prev) => ({ ...prev, ...patch })),
    [],
  );

  useEffect(() => {
    let cancelled = false;

    function mapReadiness(status: 'healthy' | 'degraded' | 'unavailable'): BackendReadiness {
      if (status === 'healthy') return 'healthy';
      if (status === 'degraded') return 'degraded';
      return 'unreachable';
    }

    async function poll() {
      try {
        const body = await fetchReady();
        if (cancelled) return;
        const detail = body.components
          ? Object.entries(body.components)
              .filter(([, c]) => c.status !== 'healthy')
              .map(([name, c]) => `${name}: ${c.status}${c.detail ? ` (${c.detail})` : ''}`)
              .join('; ') || null
          : null;
        setStatus({
          httpReachable: true,
          readiness: mapReadiness(body.status),
          readinessDetail: detail,
        });
      } catch {
        if (cancelled) return;
        setStatus({ httpReachable: false, readiness: 'unreachable', readinessDetail: null });
      }
    }

    const handle = window.setInterval(poll, 30_000);
    void poll();
    return () => {
      cancelled = true;
      window.clearInterval(handle);
    };
  }, [setStatus]);

  return (
    <BackendStatusContext.Provider value={{ status, setStatus }}>
      {children}
    </BackendStatusContext.Provider>
  );
}

